import { USER_CONTEXT, SUPER_ADMIN_ROLES, ROLES } from "../../../../constants";

// ---------------------------------------------------------------------------
// Permissions tab (profile subject must support Freedom overrides)
// ---------------------------------------------------------------------------

/** Use Freedom `roleName`, not role-type category (System Admin vs Facility Admin share a category). */
export function isPermissionsTabVisibleForProfileSubject(freedomRoleName) {
    if (freedomRoleName == null || freedomRoleName === "") return false;
    if (SUPER_ADMIN_ROLES.includes(freedomRoleName)) return false;
    if (freedomRoleName === ROLES.SYSTEM_ADMIN) return false;
    if (freedomRoleName === "Facility Admin") return false;
    return true;
}

// ---------------------------------------------------------------------------
// New UI tab factories (createStaffV2Enabled)
// ---------------------------------------------------------------------------

const tabFacilities = (allTabs) => ({ id: allTabs.Roles, label: "Facilities", icon: "flag" });

const tabLocationHistory = (allTabs) => ({
    id: allTabs.Location_History,
    label: "Facility History",
    icon: "history",
});

const tabLearnerGroups = (allTabs, icon = null) => ({
    id: allTabs.Learner_Group,
    label: "Learner Groups",
    ...(icon && { icon }),
});

const tabPermissions = (allTabs) => ({ id: allTabs.Permissions, label: "Permissions", icon: "security" });

/**
 * True when the Facilities tab would be shown in the new staff profile UI.
 * Super Admin viewing own profile, or viewing another Super Admin, has no Facilities tab.
 */
export function viewerHasFacilitiesTab({ isSuperAdmin, isViewingOwnProfile, profileIsSuperAdmin }) {
    if (isSuperAdmin) {
        if (isViewingOwnProfile) return false;
        if (profileIsSuperAdmin) return false;
        return true;
    }
    return true;
}

/**
 * Facility History is gated by `plaHistoryExperimental`.
 * Anyone who sees Facilities also sees Facility History when the flag is on.
 * Super Admin viewing their own profile has no Facilities but may still see Facility History when the flag is on (legacy product behavior).
 */
export function shouldShowLocationHistoryTab(plaHistoryExperimental, ctx) {
    if (!plaHistoryExperimental) return false;
    if (viewerHasFacilitiesTab(ctx)) return true;
    if (ctx.isSuperAdmin && ctx.isViewingOwnProfile) return true;
    return false;
}

export function buildNewTabs({
    allTabs,
    isViewingOwnProfile,
    isSuperAdmin,
    profileIsSuperAdmin,
    freedomRoleName,
    plaHistoryExperimental,
}) {
    const facilitiesContext = { isSuperAdmin, isViewingOwnProfile, profileIsSuperAdmin };
    const showLocationHistory = shouldShowLocationHistoryTab(plaHistoryExperimental, facilitiesContext);

    if (isSuperAdmin) {
        if (isViewingOwnProfile) {
            const tabs = [];
            tabs.push(tabLearnerGroups(allTabs));
            return tabs;
        }
        if (profileIsSuperAdmin) return [];
        const tabs = [tabFacilities(allTabs)];
        if (showLocationHistory) tabs.push(tabLocationHistory(allTabs));
        if (isPermissionsTabVisibleForProfileSubject(freedomRoleName)) {
            tabs.push(tabPermissions(allTabs));
        }
        return tabs;
    }

    const tabs = [tabFacilities(allTabs)];
    if (showLocationHistory) tabs.push(tabLocationHistory(allTabs));
    if (isPermissionsTabVisibleForProfileSubject(freedomRoleName)) {
        tabs.push(tabPermissions(allTabs));
    }
    if (isViewingOwnProfile) tabs.push(tabLearnerGroups(allTabs, "group"));
    return tabs;
}

// ---------------------------------------------------------------------------
// Legacy UI (pre–create staff v2)
// ---------------------------------------------------------------------------

export function buildLegacyTabs({ allTabs, isViewingOwnProfile, isSuperAdmin, isFacilityAdmin, isDisableV1MessagingEnabled }) {
    const tabs = [
        { id: allTabs.Details, label: "Details" },
        { id: allTabs.Relationship, label: "Relationship" },
        { id: allTabs.Roles, label: "Roles" },
    ];
    if (isViewingOwnProfile) tabs.push({ id: allTabs.Learner_Group, label: "Learner Groups" });
    if (isSuperAdmin || isFacilityAdmin) tabs.splice(1, 0, { id: allTabs.Communication, label: "Communication" });
    if (isDisableV1MessagingEnabled) {
        return tabs.filter((t) => t.id !== allTabs.Relationship && t.id !== allTabs.Communication);
    }
    return tabs;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolves visible staff profile tabs (new matrix UI or legacy).
 *
 * New UI (createStaffV2Enabled):
 * - Super Admin viewing own: optional Facility History (PLA flag); Learner Groups.
 * - Super Admin viewing another Super Admin: no tabs.
 * - Super Admin viewing other roles: Facilities; Facility History when PLA flag (same cohort as Facilities viewers); Permissions when subject role supports overrides.
 * - Non–Super Admin: Facilities; Facility History when PLA flag; Permissions when applicable; Learner Groups when viewing own.
 */
export function computeStaffProfileTabs({
    profileId,
    allTabs,
    global,
    isDisableV1MessagingEnabled,
    freedomRoleName,
    createStaffV2Enabled,
    plaHistoryExperimental,
}) {
    const isViewingOwnProfile = profileId != null && String(profileId) === String(USER_CONTEXT.personId);
    const isSuperAdmin = Boolean(global?.isSuperAdmin);
    const isFacilityAdmin = Boolean(global?.isFacilityAdmin);
    const profileIsSuperAdmin = freedomRoleName != null && SUPER_ADMIN_ROLES.includes(freedomRoleName);

    if (Boolean(createStaffV2Enabled)) {
        return buildNewTabs({
            allTabs,
            isViewingOwnProfile,
            isSuperAdmin,
            profileIsSuperAdmin,
            freedomRoleName,
            plaHistoryExperimental,
        });
    }

    return buildLegacyTabs({
        allTabs,
        isViewingOwnProfile,
        isSuperAdmin,
        isFacilityAdmin,
        isDisableV1MessagingEnabled,
    });
}

const useTabDetailsHook = (props) => computeStaffProfileTabs(props);

export default useTabDetailsHook;
